<template>
  <el-drawer v-model="visible" :title="title" size="560px" class="work-trend-drawer">
    <template v-if="work">
      <div class="trend-head">
        <div>
          <p class="trend-title">{{ work.title || '（无标题）' }}</p>
          <div class="trend-meta">
            <el-tag size="small">{{ work.platform_label || (work.platform || '—') }}</el-tag>
            <span>{{ work.account || '—' }}</span>
          </div>
        </div>
        <el-button
          v-if="work.link"
          link
          type="primary"
          @click="openLink"
        >打开原文</el-button>
      </div>

      <div v-if="history.length" class="trend-body">
        <p class="hint-text">历次同步观测点上的指标变化（单作品增长曲线）</p>
        <SimpleChart
          type="line"
          :labels="chart.labels"
          :series="chart.series"
          :height="240"
        />
        <el-table :data="history" size="small" stripe style="margin-top: 12px">
          <el-table-column label="观测时间" width="150">
            <template #default="{ row }">{{ formatTime(row.cutoff_at) }}</template>
          </el-table-column>
          <el-table-column prop="reads" label="阅读" width="72" />
          <el-table-column prop="likes" label="点赞" width="64" />
          <el-table-column prop="favorites" label="收藏" width="64" />
          <el-table-column label="阅读增量" width="96">
            <template #default="{ row }">
              <span v-if="row.delta_reads != null">
                <span :class="row.delta_reads >= 0 ? 'up' : 'down'">
                  {{ row.delta_reads >= 0 ? '+' : '' }}{{ row.delta_reads }}
                </span>
              </span>
              <span v-else>—</span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <el-empty
        v-else-if="!loading"
        description="暂无趋势数据"
        :image-size="64"
      >
        <p class="empty-sub">该作品暂未积累多次同步观测点，继续同步后会自动生成增长曲线。</p>
      </el-empty>
      <div v-else-if="loading" v-loading="true" class="trend-loading" />
    </template>
  </el-drawer>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import SimpleChart from './SimpleChart.vue'
import { contentAnalyticsApi } from '../api'

const formatNum = (n) => {
  const v = Number(n) || 0
  if (v >= 10000) return `${(v / 10000).toFixed(1)}万`
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`
  return String(v)
}

const formatTime = (iso) => {
  if (!iso) return '—'
  return iso.slice(0, 16).replace('T', ' ')
}

export default {
  name: 'WorkTrendDrawer',
  components: { SimpleChart },
  props: {
    modelValue: { type: Boolean, default: false },
    // 单个作品对象；缺少 work_db_id 时按 work 的当前指标生成单点
    work: { type: Object, default: null },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const loading = ref(false)
    const history = ref([])
    const resWork = ref(null)

    const visible = computed({
      get: () => props.modelValue,
      set: (v) => emit('update:modelValue', v),
    })

    const work = computed(() => resWork.value || props.work || null)
    const title = computed(() => (work.value ? '作品数据趋势' : '作品趋势'))

    const chart = computed(() => {
      const h = history.value || []
      return {
        labels: h.map((x) => formatTime(x.cutoff_at)),
        series: [
          { name: '阅读', data: h.map((x) => x.reads), color: '#409EFF' },
          { name: '点赞', data: h.map((x) => x.likes), color: '#67C23A' },
          { name: '收藏', data: h.map((x) => x.favorites), color: '#E6A23C' },
        ],
      }
    })

    const load = async () => {
      const wob = props.work
      if (!wob) return
      loading.value = true
      history.value = []
      resWork.value = null
      try {
        const dbId = Number(wob.work_db_id)
        if (dbId) {
          const res = await contentAnalyticsApi.getWorkHistory(dbId)
          resWork.value = res.work || wob
          history.value = res.history || []
        } else {
          // 无对应分析库记录的（如内容库里的单条），展示当前指标为单点
          resWork.value = { ...wob }
          history.value = wob.reads == null ? [] : [
            {
              cutoff_at: wob.metrics_as_of || new Date().toISOString(),
              reads: wob.reads,
              likes: wob.likes,
              favorites: wob.favorites,
              comments: wob.comments,
              shares: wob.shares,
              delta_reads: null,
            },
          ]
        }
      } catch {
        ElMessage.error('加载作品趋势失败')
      } finally {
        loading.value = false
      }
    }

    const openLink = () => {
      if (work.value?.link) window.open(work.value.link, '_blank')
    }

    watch(
      () => [props.modelValue, props.work],
      ([open]) => {
        if (open) load()
      },
    )

    return {
      visible,
      work,
      title,
      loading,
      history,
      chart,
      formatTime,
      formatNum,
      openLink,
      load,
    }
  },
}
</script>

<style scoped>
.trend-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.trend-title {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
  margin: 0 0 6px;
  color: var(--mp-text);
}
.trend-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--mp-text-muted);
  font-size: 12px;
}
.trend-body .hint-text {
  margin: 0 0 10px;
}
.up {
  color: var(--mp-success);
}
.down {
  color: var(--mp-danger, #f56c6c);
}
.empty-sub {
  color: var(--mp-text-muted);
  font-size: 12px;
  margin: 0;
}
.trend-loading {
  min-height: 200px;
}
</style>
